import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Banknote, Building2, Check, CheckCircle2, CreditCard, FileText, Loader2, Receipt, Search, X } from 'lucide-react';
import { call } from '@/api/desk';
import { recordPayment } from '@/api/bursar';
import { arr, dOnly, errMsg, money, newKey, today, useBase } from '@/features/shared/ops/kit';
import { PaymentReceipt } from './ReceiptsPage';

const METHODS = [
  { value: 'cash', label: 'Cash', hint: 'Paid at the office', icon: Banknote },
  { value: 'pos', label: 'POS', hint: 'Card on the school POS', icon: CreditCard },
  { value: 'bank_deposit', label: 'Bank deposit', hint: 'Teller / transfer slip', icon: Building2 },
  { value: 'cheque', label: 'Cheque', hint: 'Cheque number needed', icon: FileText },
] as const;
const inp = 'w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:border-[#0A2540] focus:ring-2 focus:ring-[#0A2540]/10 outline-none';
const name = (s: any) => s?.name ?? [s?.first_name, s?.middle_name, s?.last_name].filter(Boolean).join(' ');

function Step({ n, title, done, children }: { n: number; title: string; done?: boolean; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5">
      <div className="flex items-center gap-2.5 mb-3">
        <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${done ? 'bg-green-600 text-white' : 'bg-[#0A2540] text-white'}`}>{done ? <Check className="w-4 h-4" /> : n}</span>
        <h2 className="font-bold text-slate-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

/**
 * Money received at the school (cash, POS, bank deposit, cheque): find the child, see what is owed, tick what it pays for,
 * choose how it was paid, record — then print or share the receipt. Payments into a parent's virtual account are recorded automatically.
 */
export function RecordPaymentPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const base = useBase();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [student, setStudent] = useState<any>(null);
  const [f, setF] = useState({ amount: '', method: 'cash', payer_name: '', narration: '', paid_at: today(), slip_no: '', bank: '' });
  const [picked, setPicked] = useState<number[]>([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<null | { id: number; msg: string; amount: number }>(null);
  const [receipt, setReceipt] = useState(false);
  const key = useRef(newKey());

  const presetId = sp.get('student_id');
  useEffect(() => { if (presetId) call('get', `/school/students/${presetId}`).then((r) => setStudent(r.data)).catch(() => undefined); }, [presetId]);

  const search = useQuery({ queryKey: ['pay-student-search', q], enabled: q.trim().length >= 2 && !student, queryFn: () => call('get', '/school/students', { params: { q: q.trim(), per_page: 8 } }).then((r) => arr(r.data)) });
  const inv = useQuery({ queryKey: ['student-open-invoices', student?.id], enabled: !!student, queryFn: () => call('get', '/school/invoices', { params: { student_id: student.id, per_page: 100 } }).then((r) => arr(r.data)) });
  const open = (inv.data ?? []).filter((i: any) => Number(i.balance) > 0 && !['cancelled', 'written_off', 'paid'].includes(i.status));
  const owed = open.reduce((s: number, i: any) => s + Number(i.balance), 0);
  const pickedTotal = useMemo(() => open.filter((i: any) => picked.includes(i.id)).reduce((s: number, i: any) => s + Number(i.balance), 0), [open, picked]);
  const set = (k: keyof typeof f, v: string) => setF((o) => ({ ...o, [k]: v }));
  const amt = Number(f.amount) || 0;
  const needsSlip = f.method === 'bank_deposit' || f.method === 'cheque';

  const choose = (s: any) => { setStudent(s); setPicked([]); setQ(''); setErr(''); };
  const togglePick = (id: number) => {
    const next = picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id];
    setPicked(next);
    const t = open.filter((i: any) => next.includes(i.id)).reduce((s: number, i: any) => s + Number(i.balance), 0);
    if (t > 0) set('amount', String(t));
  };

  async function submit() {
    setErr('');
    if (!student) return setErr('Choose the student first.');
    if (amt < 1) return setErr('Enter the amount received.');
    if (f.method === 'cheque' && !f.slip_no.trim()) return setErr('Enter the cheque number.');
    setBusy(true);
    try {
      const r: any = await recordPayment({
        student_id: student.id, amount: amt, method: f.method as any, payer_name: f.payer_name || undefined, narration: f.narration || undefined,
        paid_at: f.paid_at || undefined, slip_no: f.slip_no || undefined, bank: f.bank || undefined, invoice_ids: picked.length ? picked : undefined,
      }, key.current);
      key.current = newKey();
      setDone({ id: r.data.id, msg: r.message ?? 'Payment recorded', amount: amt });
      ['payments', 'invoices', 'student-open-invoices', 'owner-dashboard'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  }
  const reset = () => { setDone(null); setStudent(null); setPicked([]); setF({ amount: '', method: 'cash', payer_name: '', narration: '', paid_at: today(), slip_no: '', bank: '' }); };

  if (done) {
    return (
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-3xl border border-slate-200 p-6 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
          <h1 className="text-2xl font-extrabold text-slate-900 mt-2">{money(done.amount)} received</h1>
          <p className="text-sm text-slate-500">{name(student)} · {student?.admission_no}</p>
          <p className="text-sm text-slate-600 mt-2">{done.msg}</p>
          <div className="grid sm:grid-cols-3 gap-2 mt-5">
            <button onClick={() => setReceipt(true)} className="py-3 rounded-xl bg-[#0A2540] text-white text-sm font-bold inline-flex items-center justify-center gap-1.5"><Receipt className="w-4 h-4" />Receipt</button>
            <button onClick={reset} className="py-3 rounded-xl border border-slate-300 text-sm font-bold">Record another</button>
            <button onClick={() => nav(`${base}/payments`)} className="py-3 rounded-xl border border-slate-300 text-sm font-bold">All payments</button>
          </div>
        </div>
        {receipt && <PaymentReceipt paymentId={done.id} onClose={() => setReceipt(false)} />}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Collections</p><h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Collect a payment</h1>
        <p className="text-sm text-slate-500">For cash, POS, bank deposits and cheques received at school. Transfers into a parent's own account number are recorded by themselves.</p></div>

      <Step n={1} title="Which student?" done={!!student}>
        {student ? (
          <div className="flex items-center gap-3 rounded-2xl bg-slate-50 border border-slate-200 p-3">
            <span className="w-11 h-11 rounded-full bg-[#0A2540] text-white font-bold grid place-items-center shrink-0">{name(student).charAt(0)}</span>
            <div className="flex-1 min-w-0"><p className="font-bold text-slate-900 truncate">{name(student)}</p><p className="text-xs text-slate-500">{student.admission_no}{student.class ? ` · ${student.class}` : ''}</p></div>
            <div className="text-right shrink-0"><p className="text-[11px] text-slate-500">Owing</p><p className={`font-extrabold ${owed > 0 ? 'text-red-600' : 'text-green-700'}`}>{inv.isLoading ? '…' : owed > 0 ? money(owed) : 'Nil'}</p></div>
            <button onClick={() => { setStudent(null); setPicked([]); }} className="p-2 text-slate-400" title="Change student"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type the name, reg number or parent's phone" className={`${inp} pl-9 py-3`} />
            {q.trim().length >= 2 && (
              <ul className="mt-2 border border-slate-200 rounded-2xl divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {search.isLoading ? <li className="p-3 text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Searching…</li>
                  : !(search.data ?? []).length ? <li className="p-3 text-sm text-slate-500">No student matches “{q}”.</li>
                  : (search.data ?? []).map((s: any) => (
                    <li key={s.id}><button onClick={() => choose(s)} className="w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 text-xs font-bold grid place-items-center">{name(s).charAt(0)}</span>
                      <span className="flex-1 min-w-0"><span className="block text-sm font-semibold text-slate-900 truncate">{name(s)}</span><span className="block text-xs text-slate-500">{s.admission_no}{s.class ? ` · ${s.class}` : ''}</span></span>
                    </button></li>
                  ))}
              </ul>
            )}
          </div>
        )}
      </Step>

      {student && (
        <Step n={2} title="What is it paying for?" done={picked.length > 0}>
          {inv.isLoading ? <p className="text-sm text-slate-500">Loading invoices…</p> : !open.length ? (
            <p className="text-sm text-slate-600 bg-slate-50 rounded-xl px-3 py-2.5">Nothing is owed. The payment will be kept as credit for the next invoice.</p>
          ) : (
            <>
              <ul className="border border-slate-200 rounded-2xl divide-y divide-slate-100">
                {open.map((i: any) => (
                  <li key={i.id}><label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer">
                    <input type="checkbox" checked={picked.includes(i.id)} onChange={() => togglePick(i.id)} className="w-4 h-4 accent-[#0A2540]" />
                    <span className="flex-1 min-w-0"><span className="block text-sm font-semibold text-slate-900">{i.title ?? i.description ?? i.number}</span><span className="block text-xs text-slate-500">{i.number}{i.due_date ? ` · due ${dOnly(i.due_date)}` : ''}{i.status === 'partial' ? ' · part-paid' : ''}</span></span>
                    <b className="text-sm">{money(i.balance)}</b>
                  </label></li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 mt-2">Tick what this payment is for. Nothing ticked: the oldest invoices are paid first.</p>
            </>
          )}
        </Step>
      )}

      {student && (
        <Step n={3} title="How much, and how was it paid?" done={amt > 0}>
          <div className="space-y-4">
            <div>
              <label className="block"><span className="text-sm font-semibold text-slate-700">Amount received (₦)</span>
                <input inputMode="decimal" value={f.amount} onChange={(e) => set('amount', e.target.value.replace(/[^\d.]/g, ''))} placeholder="0" className={`${inp} text-2xl font-extrabold py-3`} /></label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {owed > 0 && <button onClick={() => set('amount', String(owed))} className="px-3 py-1.5 rounded-full bg-slate-100 text-xs font-semibold">Full balance {money(owed)}</button>}
                {pickedTotal > 0 && pickedTotal !== owed && <button onClick={() => set('amount', String(pickedTotal))} className="px-3 py-1.5 rounded-full bg-slate-100 text-xs font-semibold">Ticked {money(pickedTotal)}</button>}
                {owed > 0 && <button onClick={() => set('amount', String(Math.round(owed / 2)))} className="px-3 py-1.5 rounded-full bg-slate-100 text-xs font-semibold">Half {money(Math.round(owed / 2))}</button>}
              </div>
              {owed > 0 && amt > owed && <p className="text-xs text-blue-700 mt-1">{money(amt - owed)} more than owed — kept as credit.</p>}
              {owed > 0 && amt > 0 && amt < owed && <p className="text-xs text-slate-500 mt-1">{money(owed - amt)} will still be owed.</p>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {METHODS.map((m) => (
                <button key={m.value} onClick={() => set('method', m.value)} className={`text-left rounded-2xl border p-3 ${f.method === m.value ? 'border-[#0A2540] ring-2 ring-[#0A2540]/10 bg-slate-50' : 'border-slate-200'}`}>
                  <m.icon className={`w-5 h-5 ${f.method === m.value ? 'text-[#0A2540]' : 'text-slate-400'}`} />
                  <p className="text-sm font-bold text-slate-900 mt-1">{m.label}</p><p className="text-[10px] text-slate-500">{m.hint}</p>
                </button>
              ))}
            </div>
            {needsSlip && (
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block"><span className="text-sm font-semibold text-slate-700">Bank</span><input maxLength={60} value={f.bank} onChange={(e) => set('bank', e.target.value)} placeholder="e.g. GTBank" className={inp} /></label>
                <label className="block"><span className="text-sm font-semibold text-slate-700">{f.method === 'cheque' ? 'Cheque number' : 'Teller / slip number'}</span><input maxLength={60} value={f.slip_no} onChange={(e) => set('slip_no', e.target.value)} className={inp} /></label>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block"><span className="text-sm font-semibold text-slate-700">Paid by <span className="font-normal text-slate-400">(optional)</span></span><input maxLength={120} value={f.payer_name} onChange={(e) => set('payer_name', e.target.value)} placeholder="e.g. Mrs Alie" className={inp} /></label>
              <label className="block"><span className="text-sm font-semibold text-slate-700">Date paid</span><input type="date" max={today()} value={f.paid_at} onChange={(e) => set('paid_at', e.target.value)} className={inp} /></label>
            </div>
            <label className="block"><span className="text-sm font-semibold text-slate-700">Note <span className="font-normal text-slate-400">(optional, on the receipt)</span></span><input maxLength={255} value={f.narration} onChange={(e) => set('narration', e.target.value)} placeholder="e.g. Second instalment" className={inp} /></label>
          </div>
        </Step>
      )}

      {err && <p className="text-sm text-red-700 bg-red-50 rounded-xl px-3 py-2.5">{err}</p>}
      {student && (
        <div className="sticky bottom-0 bg-white/95 backdrop-blur border border-slate-200 rounded-2xl p-3 flex items-center gap-3">
          <div className="flex-1 min-w-0"><p className="text-xs text-slate-500">Recording</p><p className="font-extrabold text-slate-900 truncate">{money(amt)} · {METHODS.find((m) => m.value === f.method)?.label} · {name(student)}</p></div>
          <button onClick={submit} disabled={busy || amt < 1} className="px-5 py-3 rounded-xl bg-green-600 text-white text-sm font-bold disabled:opacity-40">{busy ? 'Recording…' : 'Record payment'}</button>
        </div>
      )}
    </div>
  );
}
